// axios artık kullanılmıyor, fetch API kullanıyoruz
import { mockApiResponse, generateUpdatedPosition } from '../data/mockData';

/**
 * API service for boat tracking application
 * Handles race data fetching and error handling
 */
// Ortam kontrolü - Vercel'de çalışıyorsa veya HTTPS ise proxy kullan
const isVercelEnvironment = typeof window !== 'undefined' && window.location.hostname.includes('vercel.app');
const isHttpsEnvironment = typeof window !== 'undefined' && window.location.protocol === 'https:';
const isProduction = isVercelEnvironment || isHttpsEnvironment;

// API endpoint'i - üretim ortamında proxy kullan, geliştirme ortamında doğrudan API'ye bağlan
const API_ENDPOINT = isProduction
  ? '/api/race'
  : 'http://mavivatan.imarine.com.tr:3583/api/v1/race';

// Mock veri kullanımını kapatıyoruz, artık proxy kullanıyoruz
const USE_MOCK_DATA = false;

// Store the last mock response for simulation
let lastMockResponse = { ...mockApiResponse };

// Store the last real API response for comparison
// eslint-disable-next-line no-unused-vars
let lastApiData = null; // ESLint uyarısını devre dışı bıraktık

// Axios ayarları - API isteği için kullanılıyor
// Not: Bu ayarlar doğrudan axios.get() içinde kullanıldığı için değişken olarak kullanılmıyor
// ESLint uyarısını önlemek için yorum satırına alındı
/*
const axiosConfig = {
  timeout: 10000, // 10 seconds
  headers: {
    'Content-Type': 'application/json',
    'Cache-Control': 'no-cache',
    'Pragma': 'no-cache'
  }
};
*/

/**
 * Fetch current race data from the API
 * @param {boolean} force - Force fetching fresh data from API
 * @returns {Promise} Promise with race data or error
 */
export const fetchRaceData = async (force = false) => {
  // Use mock data for development
  if (USE_MOCK_DATA) {
    // Generate updated position data
    lastMockResponse = generateUpdatedPosition(lastMockResponse);
    
    return {
      data: lastMockResponse,
      status: 'connected',
      timestamp: new Date()
    };
  }
  
  // Real API call - tamamen yeni yaklaşım
  try {
    // Doğrudan Fetch API kullan - Axios yerine
    const timestamp = new Date().getTime();
    const randomStr = Math.random().toString(36).substring(7);
    const apiUrl = `${API_ENDPOINT}?t=${timestamp}&nocache=${randomStr}`;
    
    console.log('API isteği yapılıyor:', apiUrl);
    
    // Fetch API ile istek yap
    const response = await fetch(apiUrl, {
      method: 'GET',
      headers: {
        'Cache-Control': 'no-cache, no-store, must-revalidate',
        'Pragma': 'no-cache',
        'Expires': '0'
      },
      cache: 'no-store'
    });
    
    // Yanıt durumunu kontrol et
    if (!response.ok) {
      throw new Error(`API isteği başarısız: ${response.status}`);
    }
    
    // JSON yanıtı al
    const data = await response.json();
    console.log('API yanıtı:', data);
    
    // API yanıtının doğru yapıda olup olmadığını kontrol et
    if (!data || !data.race || !data.tracks || !data.tracks.length) {
      throw new Error('API yanıtı geçerli veri içermiyor');
    }
    
    // Şu anki zamanı kullan
    const apiTimestamp = new Date();
    
    // Her zaman yeni veri olarak işaretle
    const isNewData = true;
    
    // API'den gelen veriyi detaylı olarak logla
    console.log('API den gelen güncel veri:', {
      zaman: apiTimestamp.toLocaleTimeString(),
      konum: [data.tracks[0].lat, data.tracks[0].lon],
      hız: data.tracks[0].sog_kn,
      yön: data.tracks[0].cog_deg,
      mesafe: data.tracks[0].distance_m
    });
    
    // Son API verisini güncelle (deep copy ile)
    lastApiData = JSON.parse(JSON.stringify(data));
    
    return {
      data: data,
      status: 'connected',
      timestamp: apiTimestamp,
      isNewData: isNewData
    };
  } catch (error) {
    console.error('API hatası:', error);
    console.error('Hata detayları:', {
      mesaj: error.message,
      kod: error.code,
      url: error.url,
      stack: error.stack
    });
    
    // Fallback to mock data if API fails
    if (USE_MOCK_DATA) {
      console.log('API hatası nedeniyle mock veri kullanılıyor...');
      lastMockResponse = generateUpdatedPosition(lastMockResponse);
      
      return {
        data: lastMockResponse,
        status: 'connected_fallback',
        timestamp: new Date(),
        error: error.message
      };
    }
    
    return {
      data: null,
      status: 'error',
      timestamp: new Date(),
      error: error.message
    };
  }
};

/**
 * Validate maritime position to ensure it's on valid sea route
 * @param {number} lat - Latitude
 * @param {number} lon - Longitude
 * @returns {Object} Validation result
 */
export const validateMaritimePosition = (lat, lon) => {
  // Maritime route boundaries for Hopa-İskenderun route
  const maritimeRoute = {
    blackSea: {
      bounds: {north: 42.5, south: 41.0, east: 42.0, west: 27.5},
    },
    bosphorus: {
      northern_entrance: {lat: 41.22897, lng: 29.11507},
      southern_exit: {lat: 41.0, lng: 29.0},
    },
    marmara: {
      bounds: {north: 40.9, south: 40.0, east: 29.5, west: 26.0},
    },
    dardanelles: {
      entrance: {lat: 40.04150, lng: 26.19118},
      exit: {lat: 39.9, lng: 26.1},
    },
    aegean: {
      bounds: {north: 40.5, south: 36.0, east: 28.5, west: 24.0},
    },
    mediterranean: {
      bounds: {north: 37.0, south: 35.5, east: 37.0, west: 27.0},
    }
  };

  // Basic validation checking if point is within any of the maritime regions
  // In a real application, this would be more sophisticated with proper geo-calculations
  const isInBlackSea = lat <= maritimeRoute.blackSea.bounds.north && 
                      lat >= maritimeRoute.blackSea.bounds.south && 
                      lon <= maritimeRoute.blackSea.bounds.east && 
                      lon >= maritimeRoute.blackSea.bounds.west;
                      
  const isInMarmara = lat <= maritimeRoute.marmara.bounds.north && 
                     lat >= maritimeRoute.marmara.bounds.south && 
                     lon <= maritimeRoute.marmara.bounds.east && 
                     lon >= maritimeRoute.marmara.bounds.west;
                     
  const isInAegean = lat <= maritimeRoute.aegean.bounds.north && 
                    lat >= maritimeRoute.aegean.bounds.south && 
                    lon <= maritimeRoute.aegean.bounds.east && 
                    lon >= maritimeRoute.aegean.bounds.west;
                    
  const isInMediterranean = lat <= maritimeRoute.mediterranean.bounds.north && 
                           lat >= maritimeRoute.mediterranean.bounds.south && 
                           lon <= maritimeRoute.mediterranean.bounds.east && 
                           lon >= maritimeRoute.mediterranean.bounds.west;
  
  const isInWater = isInBlackSea || isInMarmara || isInAegean || isInMediterranean;
  
  return {
    isInWater,
    maritimeArea: isInBlackSea ? 'blackSea' : 
                  isInMarmara ? 'marmara' : 
                  isInAegean ? 'aegean' : 
                  isInMediterranean ? 'mediterranean' : 'unknown',
    isValidRoute: isInWater // For now, just checking if in water
  };
};

/**
 * Calculate distance between two coordinates in nautical miles
 * Uses Haversine formula
 * @param {Array} coord1 - [lat1, lon1]
 * @param {Array} coord2 - [lat2, lon2]
 * @returns {number} Distance in nautical miles
 */
export const calculateDistance = (coord1, coord2) => {
  const [lat1, lon1] = coord1;
  const [lat2, lon2] = coord2;
  
  const R = 6371; // Earth's radius in km
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  
  const a = Math.sin(dLat/2) * Math.sin(dLat/2) +
          Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * 
          Math.sin(dLon/2) * Math.sin(dLon/2);
          
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
  const distance = R * c;
  
  // Convert km to nautical miles (1 nm = 1.852 km)
  return distance / 1.852;
};

/**
 * Check if current position is near a checkpoint
 * @param {Array} currentPosition - [lat, lon]
 * @param {Object} checkpoint - Checkpoint data with coordinates
 * @param {number} proximityRadius - Radius in NM to consider "at checkpoint"
 * @returns {boolean} Whether position is at checkpoint
 */
export const isAtCheckpoint = (currentPosition, checkpoint, proximityRadius = 0.5) => {
  const distance = calculateDistance(
    currentPosition, 
    [checkpoint.coordinates.lat, checkpoint.coordinates.lng]
  );
  
  return distance <= proximityRadius;
};

/**
 * Format duration in seconds to human-readable string
 * @param {number} seconds - Duration in seconds
 * @returns {string} Formatted duration (e.g., "1d 2h 30m")
 */
export const formatDuration = (seconds) => {
  const days = Math.floor(seconds / (24 * 60 * 60));
  seconds -= days * 24 * 60 * 60;
  
  const hours = Math.floor(seconds / (60 * 60));
  seconds -= hours * 60 * 60;
  
  const minutes = Math.floor(seconds / 60);
  
  let result = '';
  if (days > 0) result += `${days}d `;
  if (hours > 0 || days > 0) result += `${hours}h `;
  result += `${minutes}m`;
  
  return result;
};

const apiService = {
  fetchRaceData,
  validateMaritimePosition,
  calculateDistance,
  isAtCheckpoint,
  formatDuration
};

export default apiService;
